import type { RJSFSchema } from '@rjsf/utils';
import { memo, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { OutputValueType } from '../../../../utils/calculated-value';
import { TypographyVariant } from '../../../../utils/theme';
import CalculatedValue from '../../../calculated-value/calculated-value';
import { Input, InputVariant } from '../../../input';
import Slider from '../../../slider/slider';
import Typography from '../../../typography/typography';
import { getPricingAmount } from '../../values/utils';

export interface SubPrice {
  pricingFormula?: string;
  employeeContribution?: number;
}

interface SubPriceComponentProps {
  subPrice: SubPrice;
  properties: RJSFSchema;
  readonly: boolean;
  index: number;
  onChangePricingFormula: (value: string, index: number) => void;
  onChangeEmployeeContribution: (value: number, index: number) => void;
}

const SubPriceComponent = ({
  subPrice,
  properties,
  readonly,
  index,
  onChangeEmployeeContribution,
  onChangePricingFormula,
}: SubPriceComponentProps) => {
  const inputId = useId();
  const { t } = useTranslation();
  const isPrice =
    subPrice.pricingFormula && Number(subPrice.pricingFormula) > 0;

  const { pricingFormula, employeeContribution } = properties ?? {};
  const currency = pricingFormula.properties.currency.default as string;

  const amountValue = useCallback(
    (value: number) => {
      return [
        {
          label: t('words.amount', { defaultValue: 'Amount' }),
          value: isPrice ? value : undefined,
          outputValueType: OutputValueType.MONEY,
          outputValueSymbol: currency,
        },
      ];
    },
    [currency, isPrice, t]
  );

  const amountAndPercentageValues = useCallback(
    (value: number) => {
      return [
        {
          label: t('words.percentage', {
            defaultValue: 'Percentage',
          }),
          value: isPrice
            ? 100 - (subPrice.employeeContribution ?? 0)
            : undefined,
          outputValueType: OutputValueType.PERCENTAGE,
          outputValueSymbol: '%',
        },
        ...amountValue(value),
      ];
    },
    [amountValue, isPrice, subPrice.employeeContribution, t]
  );

  return (
    <Wrapper>
      <Input
        name={pricingFormula.title}
        width="100%"
        value={isPrice ? subPrice.pricingFormula : undefined}
        variant={InputVariant.Amount}
        disabled={readonly}
        onChange={(event) => onChangePricingFormula(event.target.value, index)}
        currency={currency}
        min={0}
      />
      <div>
        <SliderContainer>
          <Slider
            label={employeeContribution.title}
            isPercentage={true}
            inputId={inputId}
            disabled={employeeContribution.disabled || !isPrice || readonly}
            value={isPrice ? subPrice.employeeContribution : undefined}
            min={0}
            max={100}
            step={0.5}
            onChange={(value) => onChangeEmployeeContribution(value, index)}
          />
        </SliderContainer>
        <CalculatedValue
          values={amountValue(
            getPricingAmount(
              subPrice.pricingFormula,
              subPrice.employeeContribution,
              true
            )
          )}
        />
      </div>
      <div>
        <StyledTypography variant={TypographyVariant.bodySm2}>
          {employeeContribution.description}
        </StyledTypography>
        <CalculatedValue
          values={amountAndPercentageValues(
            getPricingAmount(
              subPrice.pricingFormula,
              subPrice.employeeContribution
            )
          )}
        />
      </div>
    </Wrapper>
  );
};

export default memo(SubPriceComponent);

const Wrapper = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(4)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `}
`;

const StyledTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(1)};
  `}
`;

const SliderContainer = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(-2)};
  `}
`;
